import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Box, Button, Typography } from "@mui/material";
import DialogTextField from "components/common/inputs/DialogTextField.js";

import { selectScenarioInfo } from "store/reducers/selectors";
import BaseDataEntryDialog from "./BaseDataEntryDialog.jsx";

const global_configs = require("store/data/global_configs.json");
const MAX_LEN = global_configs.data["scenario-name"]["max-length"];

/**
 * Dialog component for renaming a scenario.
 *
 * Displays a modal dialog that allows the user to enter a new name for a scenario,
 * validates the input length, and provides Save and Cancel actions.
 *
 * @param {Object} props - Component props.
 * @param {boolean} props.open - Whether the dialog is open.
 * @param {function(string): void} props.handleSaveAndClose - Callback invoked with the new scenario name when saving.
 * @param {function(): void} props.handleCancel - Callback invoked when cancelling the dialog.
 * @returns {JSX.Element} The RenameScenarioDialog component.
 */
export default function RenameScenarioDialog({ open, handleSaveAndClose, handleCancel }) {
  const scenario = useSelector(selectScenarioInfo);
  const initialValue = (scenario && scenario.scenarioName) || "";

  const [textEntry, setTextEntry] = React.useState(initialValue);
  const [textChanged, setTextChanged] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setTextEntry(initialValue);
    setTextChanged(false);
  }, [open, initialValue]);

  const trimmedValue = textEntry.trim();
  const hasValue = Boolean(trimmedValue);
  const isTooLong = trimmedValue.length > MAX_LEN;
  const isValid = hasValue && !isTooLong;

  const handleSave = () => {
    if (!isValid) {
      setTextChanged(true);
      return;
    }
    handleSaveAndClose(trimmedValue);
  };

  const helperText = (() => {
    if (textChanged && !hasValue) {
      return "Scenario name is required.";
    }
    if (textChanged && isTooLong) {
      return `Maximum ${MAX_LEN} characters allowed.`;
    }
    return `${trimmedValue.length}/${MAX_LEN}`;
  })();

  const showError = (textChanged && !hasValue) || (textChanged && isTooLong);

  return (
    <BaseDataEntryDialog
      open={open}
      onClose={handleCancel}
      title="Rename scenario"
      description="Choose a short, descriptive name so you can find this scenario later."
      maxWidth="xs"
      titleId="rename-scenario-dialog-title"
      descriptionId="rename-scenario-dialog-description"
      actions={
        <>
          <Button
            onClick={handleCancel}
            variant="outlined"
            sx={{
              borderColor: "primary.main",
              color: "primary.main",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                borderColor: "primary.extraDark",
                backgroundColor: "primary.extraLight",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!isValid}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              px: 2.25,
            }}
          >
            Save
          </Button>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <DialogTextField
          label="Scenario name"
          value={textEntry}
          onChange={(event) => {
            setTextEntry(event.target.value);
            setTextChanged(true);
          }}
          // Dialog-scoped autofocus: focusing the primary input when a modal
          // opens follows the WAI-ARIA dialog pattern (MUI traps focus).
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          fullWidth
          size="small"
          inputProps={{ maxLength: MAX_LEN }}
          error={Boolean(showError)}
          helperText={
            <Typography
              component="span"
              variant="caption"
              sx={{ color: showError ? "error.text" : "text.secondary" }}
            >
              {helperText}
            </Typography>
          }
        />
      </Box>
    </BaseDataEntryDialog>
  );
}

RenameScenarioDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleSaveAndClose: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired,
};
